import * as React from "react";

import Form, { FormComponentProps } from "@ant-design/compatible/es/form";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import FormCard from "@impero-com/common/components/formCard";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import {
    buildUserDictionary,
    confirmNormalPassword,
    Callback,
} from "@impero-com/common/utils/confirmPassword";
import { t } from "@impero-com/common/utils/i18n";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import { Button, emitNotification, Modal } from "@impero-com/ui";
import { Input, Popover } from "antd";

import { postChangePassword } from "api/generated/authentication/authentication";
import { Authenticated } from "api/generated/model";
import { intoUnsafeValidationRules } from "utils/form";

import "main.css";
import { ENVIRONMENT } from "../../env";

import * as theme from "./theme.css";

const FormItem = Form.Item;

class ChangePasswordInternal extends React.Component<
    ChangePasswordProps,
    ChangePasswordState
> {
    private captchaRef: React.RefObject<HCaptcha | null>;

    constructor(props: ChangePasswordProps) {
        super(props);
        this.captchaRef = React.createRef();

        this.state = {
            confirmDirty: false,
            status: { type: "DEFAULT" },
        };
    }

    handleSubmit = () => {
        this.props.form.validateFields(
            (err: any, values: ChangePasswordProps) => {
                if (!err) {
                    const { oldPassword, password, captcha } = values;
                    const { ajaxManager } = this.props;
                    ajaxManager
                        .ajax<Authenticated, ChangePasswordStatus>(
                            // Those variables can never be null because they are required in the form
                            () =>
                                postChangePassword({
                                    oldPassword: oldPassword ?? "",
                                    password: password ?? "",
                                    humanVerificationResponse: captcha ?? "",
                                }),
                            {
                                component: this,
                                initialStatusValue: "DEFAULT",
                                getSuccessStatus: () => ({ type: "DEFAULT" }),
                                getErrorStatus: () => ({ type: "DEFAULT" }),
                                inFlightStatus: { type: "SUBMITTING" },
                                onErrorCode: {
                                    400: () => {
                                        emitNotification(
                                            "error",
                                            t(
                                                "ChangePassword_NewPassword_Invalid"
                                            )
                                        );
                                        if (this.captchaRef.current) {
                                            this.captchaRef.current.resetCaptcha();
                                        }
                                    },
                                    401: () => {
                                        emitNotification(
                                            "error",
                                            t(
                                                "ChangePassword_OldPassword_Incorrect"
                                            )
                                        );
                                        if (this.captchaRef.current) {
                                            this.captchaRef.current.resetCaptcha();
                                        }
                                    },
                                },
                            }
                        )
                        .then(() => {
                            emitNotification(
                                "success",
                                t("ChangePassword_PasswordChanged")
                            );
                            setTimeout(() => this.props.onCloseDialog(), 2000);
                        });
                }
            }
        );
    };

    handleConfirmPassword = (rule: any, value: string, callback: Callback) => {
        const { orgCookie, user } = this.props;
        const userDictionary = buildUserDictionary(
            orgCookie,
            user ? user : undefined
        );
        confirmNormalPassword(rule, value, callback, userDictionary);
        this.validateToNextPassword(rule, value, callback);
    };

    compareToFirstPassword = (
        rule: any,
        value: any,
        callback: (msg?: string) => void
    ) => {
        const { form } = this.props;
        if (value && value !== form.getFieldValue("password")) {
            callback(t("PasswordRepeatIncorrect"));
        } else {
            callback();
        }
    };

    validateToNextPassword = (rule: any, value: any, callback: () => void) => {
        const form = this.props.form;
        if (value && this.state.confirmDirty) {
            form.validateFields(["confirm"], { force: true });
        }
        callback();
    };

    handleConfirmBlur = (e: any) => {
        const value = e.target.value;
        this.setState({ confirmDirty: this.state.confirmDirty || !!value });
    };

    hasErrors(fieldsError: object) {
        return Object.values(fieldsError).some((err) => err);
    }

    render() {
        const { getFieldDecorator, getFieldsError } = this.props.form;
        const PasswordStrengthPopover = (
            <div className={theme.passwordPopover}>
                <p>{t("PasswordRule1")}</p>
                <p>{t("PasswordRule2")}</p>
                <p>{t("PasswordRule3")}</p>
                <p>{t("PasswordRule4")}</p>
            </div>
        );

        const oldPasswordName = t("ChangePassword_OldPassword");

        return (
            <Modal
                variant="content"
                open
                onClose={this.handleCloseDialog}
                title={t("ChangePassword_Title")}
                width={590}
                className={theme.dialog}
                footer={
                    <>
                        <Button onClick={this.handleCloseDialog}>
                            {t("Cancel")}
                        </Button>
                        <Button
                            variant="primary"
                            disabled={this.hasErrors(getFieldsError())}
                            loading={this.state.status.type === "SUBMITTING"}
                            onClick={this.handleSubmit}
                        >
                            {t("ChangePassword_Button")}
                        </Button>
                    </>
                }
            >
                <FormCard onSubmitForm={this.handleSubmit}>
                    <FormItem>
                        {getFieldDecorator("oldPassword", {
                            rules: intoUnsafeValidationRules(
                                [
                                    {
                                        kind: "ruleset",
                                        required: true,
                                    },
                                ],
                                oldPasswordName
                            ),
                        })(
                            <Input
                                type="password"
                                placeholder={oldPasswordName}
                                onBlur={this.handleOldPasswordBlur}
                            />
                        )}
                    </FormItem>
                    <FormItem>
                        {getFieldDecorator("password", {
                            rules: [{ validator: this.handleConfirmPassword }],
                        })(
                            <Input
                                type="password"
                                placeholder={t("ChangePassword_NewPassword")}
                                onBlur={this.handleConfirmBlur}
                            />
                        )}
                    </FormItem>
                    <FormItem>
                        {getFieldDecorator("confirm", {
                            rules: [{ validator: this.compareToFirstPassword }],
                        })(
                            <Input
                                type="password"
                                placeholder={t(
                                    "ChangePassword_NewPasswordRepeat"
                                )}
                                onBlur={this.handleConfirmBlur}
                            />
                        )}
                    </FormItem>
                    <FormItem>
                        {getFieldDecorator("captcha", {
                            rules: [{ required: true }],
                        })(
                            <div className={theme.hcaptchaContainer}>
                                <HCaptcha
                                    size="normal"
                                    sitekey={
                                        ENVIRONMENT.IMPERO_HCAPTCHA_SITE_KEY
                                    }
                                    onVerify={this.handleCaptchaVerify}
                                    ref={this.captchaRef}
                                />
                            </div>
                        )}
                    </FormItem>
                </FormCard>

                <Popover
                    content={PasswordStrengthPopover}
                    placement="bottom"
                    trigger="click"
                >
                    <Button id="password-rules-button" variant="link">
                        {t("PasswordRules")}
                    </Button>
                </Popover>
            </Modal>
        );
    }

    private handleOldPasswordBlur = (_e: any) => {
        this.props.form.validateFields(["oldPassword"]);
    };

    private handleCloseDialog = () => {
        const { onCloseDialog } = this.props;
        const { status } = this.state;

        if (status.type !== "SUBMITTING") {
            onCloseDialog();
        }
    };

    private handleCaptchaVerify = (captcha: string | null) => {
        this.props.form.setFieldsValue({
            captcha,
        });
    };
}

interface ChangePasswordProps extends FormComponentProps {
    oldPassword?: string;
    password?: string;
    captcha?: string;
    ajaxManager: AjaxManager;
    orgCookie: OrgCookie;
    user: User | null;
    onCloseDialog: () => void;
}

interface User {
    email: string;
    firstName: string;
    lastName: string;
}

type ChangePasswordStatus = { type: "DEFAULT" } | { type: "SUBMITTING" };

interface ChangePasswordState {
    status: ChangePasswordStatus;
    confirmDirty?: boolean;
}

const ChangePassword = Form.create<ChangePasswordProps>()(
    ChangePasswordInternal
);

export { ChangePassword };
